import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { match, isInDateRange } from "./utils";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  filter: Boundaries["retrieveUnavailabilities"]["request"],
): State {
  const index = state.findIndex((req) => match(req.filter, filter));

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { filter, request: setRequestStateToPending(), unavailabilities: [] },
      ];
}

function handleSuccess(
  state: State,
  {
    unavailabilities,
    filter,
  }: Boundaries["retrieveUnavailabilities"]["success"],
): State {
  const index = state.findIndex((req) => match(req.filter, filter));

  return index !== -1
    ? [
        ...state.slice(0, index),
        { filter, request: setRequestStateToSuccess(), unavailabilities },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { filter, request: setRequestStateToSuccess(), unavailabilities },
      ];
}

function handleFailure(
  state: State,
  filter: Boundaries["retrieveUnavailabilities"]["failure"],
): State {
  const index = state.findIndex((req) => match(req.filter, filter));

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { filter, request: setRequestStateToFailure(), unavailabilities: [] },
      ];
}

function handleCreateUnavailability(
  state: State,
  { unavailability }: Boundaries["createUnavailability"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    unavailabilities:
      isInDateRange(req.filter, unavailability.startDate) &&
      isInDateRange(
        req.filter,
        unavailability.endDate || unavailability.startDate,
      )
        ? [...req.unavailabilities, unavailability]
        : req.unavailabilities,
  }));
}

function handleEditUnavailability(
  state: State,
  unavailability: Boundaries["editUnavailability"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    unavailabilities: req.unavailabilities.map((u) =>
      u.uuid === unavailability.uuid ? unavailability : u,
    ),
  }));
}

function handleRemoveUnavailability(
  state: State,
  unavailability: Boundaries["editUnavailability"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    unavailabilities: req.unavailabilities.filter(
      (u) => u.uuid !== unavailability.uuid,
    ),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveUnavailabilitiesRequested]: handleRequest,
    [events.retrieveUnavailabilitiesSucceeded]: handleSuccess,
    [events.retrieveUnavailabilitiesFailed]: handleFailure,
    [events.createUnavailabilitySucceeded]: handleCreateUnavailability,
    [events.editUnavailabilitySucceeded]: handleEditUnavailability,
    [events.removeUnavailabilitySucceeded]: handleRemoveUnavailability,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
